//frontend/src/pages/gro/ActionPlanExecutionBoardPage.tsx
import { useEffect, useMemo, useState, type ReactNode } from "react";
import {
  closestCorners,
  DndContext,
  DragOverlay,
  PointerSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from "@dnd-kit/core";
import {
  AlertTriangle,
  CalendarClock,
  Filter,
  GripVertical,
  Search,
  UserRound,
} from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import { CrudRowActions } from "@/components/shared/CrudRowActions";
import { PageHeader } from "@/components/shared/PageHeader";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Textarea } from "@/components/ui/textarea";
import {
  useActionPlanExecutionBoard,
  useActionPlanExecutionItemAuditLogs,
  useActionPlanExecutionResponsibleUsers,
  useActionPlans,
  useCreateActionPlanExecutionItemAuditLog,
} from "@/hooks/useActionPlans";
import {
  useRiskInventories,
  useSetInventoryItemActionPlanItemStatus,
  useUpdateInventoryItemActionPlanItem,
} from "@/hooks/useRiskInventories";
import type {
  ActionPlanExecutionAuditLog,
  ActionPlanExecutionBoardItem,
  OperationalBoardStatus,
} from "@/types/actionPlans";
import type {
  OperationalPlanPriority,
  RiskInventoryOperationalActionPlanItemUpdateIn,
} from "@/types/riskInventory";

const COLUMNS: { key: OperationalBoardStatus; title: string }[] = [
  { key: "not_started", title: "Não iniciado" },
  { key: "in_progress", title: "Em andamento" },
  { key: "completed", title: "Concluído" },
  { key: "cancelled", title: "Cancelado" },
];

function formatDateBR(value?: string | null) {
  if (!value) return "-";
  const [year, month, day] = value.slice(0, 10).split("-");
  if (!year || !month || !day) return value;
  return `${day}/${month}/${year}`;
}

function formatDateTimeBR(value?: string | null) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(date);
}

function priorityLabel(priority: string) {
  if (priority === "critical") return "Crítica";
  if (priority === "high") return "Alta";
  if (priority === "low") return "Baixa";
  return "Média";
}

function statusLabel(status: OperationalBoardStatus) {
  if (status === "in_progress") return "Em andamento";
  if (status === "completed") return "Concluído";
  if (status === "cancelled") return "Cancelado";
  return "Não iniciado";
}

function typeLabel(action: string) {
  const normalized = (action || "").toLowerCase();
  if (normalized === "create") return "Incluiu";
  if (normalized === "delete") return "Deletou";
  return "Editou";
}

function fieldLabel(key: string) {
  const normalized = key.toLowerCase();
  if (normalized === "status") return "STATUS";
  if (normalized.includes("responsible")) return "Responsável";
  if (normalized === "title") return "Título";
  if (normalized === "description") return "Descrição";
  if (normalized === "priority") return "Prioridade";
  if (normalized === "start_date") return "Início";
  if (normalized === "due_date") return "Prazo";
  if (normalized === "expected_result") return "Resultado esperado";
  return key;
}

function valueLabel(key: string, value: unknown) {
  if (value === null || value === undefined || value === "") return "-";
  const normalized = key.toLowerCase();
  if (normalized === "status" && typeof value === "string") return statusLabel(value as OperationalBoardStatus);
  if (normalized === "priority" && typeof value === "string") return priorityLabel(value);
  if (["start_date", "due_date", "completed_at"].includes(normalized) && typeof value === "string") return formatDateBR(value);
  return String(value);
}

function parseAuditBlock(log: ActionPlanExecutionAuditLog, bucket: "previous" | "current") {
  const eventData = (log.event_data || {}) as Record<string, unknown>;
  const explicit = eventData[bucket];
  if (explicit && typeof explicit === "object" && !Array.isArray(explicit)) {
    const entries = Object.entries(explicit as Record<string, unknown>).filter(([, value]) => value !== undefined);
    if (!entries.length) return "-";
    return entries.map(([key, value]) => `${fieldLabel(key)}: ${valueLabel(key, value)}`).join("\n");
  }

  if (bucket === "previous") {
    if (eventData.previous_status || eventData.previous_responsible_user_name) {
      const lines = [];
      if (eventData.previous_status) lines.push(`STATUS: ${valueLabel("status", eventData.previous_status)}`);
      if (eventData.previous_responsible_user_name) lines.push(`Responsável: ${String(eventData.previous_responsible_user_name)}`);
      return lines.join("\n") || "-";
    }
    return log.action === "create" ? "-" : "-";
  }

  if (eventData.new_status || eventData.new_responsible_user_name) {
    const lines = [];
    if (eventData.new_status) lines.push(`STATUS: ${valueLabel("status", eventData.new_status)}`);
    if (eventData.new_responsible_user_name) lines.push(`Responsável: ${String(eventData.new_responsible_user_name)}`);
    return lines.join("\n") || "-";
  }

  return log.action === "delete" ? "-" : "-";
}

function hasMeaningfulAuditData(log: ActionPlanExecutionAuditLog) {
  const eventData = (log.event_data || {}) as Record<string, unknown>;
  const previous = eventData.previous;
  const current = eventData.current;
  if (previous && typeof previous === "object" && Object.keys(previous as Record<string, unknown>).length) return true;
  if (current && typeof current === "object" && Object.keys(current as Record<string, unknown>).length) return true;
  if (eventData.previous_status !== undefined && eventData.previous_status !== eventData.new_status) return true;
  if (eventData.previous_responsible_user_name !== undefined && eventData.previous_responsible_user_name !== eventData.new_responsible_user_name) return true;
  return false;
}

function getDeadlineBadge(card: ActionPlanExecutionBoardItem) {
  if (card.is_overdue) {
    return <Badge className="border-red-200 bg-red-50 text-red-700 hover:bg-red-50">Vencida</Badge>;
  }

  return <Badge variant="outline">Prazo {formatDateBR(card.due_date)}</Badge>;
}

function SmallStat({ label, value }: { label: string; value: number }) {
  return (
    <Card className="border-muted/60 shadow-sm">
      <CardContent className="px-3 py-2">
        <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">{label}</div>
        <div className="mt-1 text-base font-semibold leading-none">{value}</div>
      </CardContent>
    </Card>
  );
}

type Filters = {
  inventory_id: string;
  action_plan_id: string;
  status: string;
  priority: string;
  responsible_user_id: string;
  deadline_state: string;
  q: string;
};

const INITIAL_FILTERS: Filters = {
  inventory_id: "all",
  action_plan_id: "all",
  status: "all",
  priority: "all",
  responsible_user_id: "all",
  deadline_state: "all",
  q: "",
};

function cardTransformStyle(transform?: { x: number; y: number } | null) {
  if (!transform) return undefined;
  return {
    transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
    touchAction: "none",
  } as const;
}

function ActionBoardCard({
  card,
  onDetail,
  onEdit,
  onLogs,
  isOverlay = false,
  isHighlighted = false,
}: {
  card: ActionPlanExecutionBoardItem;
  onDetail: (card: ActionPlanExecutionBoardItem) => void;
  onEdit: (card: ActionPlanExecutionBoardItem) => void;
  onLogs: (card: ActionPlanExecutionBoardItem) => void;
  isOverlay?: boolean;
  isHighlighted?: boolean;
}) {
  const draggable = !isOverlay
    ? useDraggable({
        id: `card:${card.id}`,
        data: { cardId: card.id, status: card.status },
      })
    : null;

  const dragStyle = isOverlay
    ? undefined
    : cardTransformStyle(draggable?.transform as { x: number; y: number } | null | undefined);

  const dragging = !isOverlay && draggable?.isDragging;

  return (
    <div
      ref={isOverlay ? undefined : draggable?.setNodeRef}
      style={dragStyle}
      {...(!isOverlay ? draggable?.listeners : {})}
      {...(!isOverlay ? draggable?.attributes : {})}
      className={`select-none rounded-2xl border border-border/70 bg-background p-3 shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md ${
        isOverlay
          ? "rotate-1 border-primary/20 shadow-2xl ring-2 ring-primary/10"
          : dragging
            ? "cursor-grabbing opacity-40"
            : isHighlighted
              ? "border-primary/40 ring-2 ring-primary/20"
              : "cursor-grab active:cursor-grabbing"
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-start gap-2">
            <div
              aria-label="Arrastar ação"
              className={`mt-0.5 shrink-0 rounded-md p-0.5 text-muted-foreground ${isOverlay ? "cursor-grabbing" : "cursor-grab active:cursor-grabbing"}`}
            >
              <GripVertical className="h-4 w-4" />
            </div>
            <button
              className="line-clamp-2 text-left text-sm font-semibold leading-5 hover:text-primary"
              onClick={() => onDetail(card)}
              type="button"
            >
              {card.title}
            </button>
          </div>
          <p className="mt-1 truncate text-xs text-muted-foreground">{card.plan_title}</p>
          <p className="truncate text-xs text-muted-foreground">{card.inventory_item_title}</p>
        </div>
        <Badge variant="outline" className="rounded-full">
          {priorityLabel(card.priority)}
        </Badge>
      </div>

      <div className="mt-3 flex flex-wrap gap-2">
        <Badge variant="secondary" className="rounded-full">
          {card.item_origin === "template" ? "Template" : "Manual"}
        </Badge>
        {getDeadlineBadge(card)}
      </div>

      <div className="mt-3 grid gap-1.5 text-xs text-muted-foreground">
        <div className="flex items-center gap-2">
          <CalendarClock className="h-3.5 w-3.5" />
          <span>
            Início {formatDateBR(card.start_date)} • Prazo {formatDateBR(card.due_date)}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <UserRound className="h-3.5 w-3.5" />
          <span>{card.responsible_user_name || "-"}</span>
        </div>
        {card.risk_name ? (
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-3.5 w-3.5" />
            <span className="truncate">{card.risk_name}</span>
          </div>
        ) : null}
      </div>

      {!isOverlay ? (
        <div className="mt-3 flex items-center justify-end">
          <CrudRowActions onView={() => onDetail(card)} onEdit={() => onEdit(card)} onLogs={() => onLogs(card)} />
        </div>
      ) : null}
    </div>
  );
}

export default function ActionPlanExecutionBoardPage() {
  const qc = useQueryClient();
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));
  const [filters, setFilters] = useState<Filters>(INITIAL_FILTERS);
  const [detailCard, setDetailCard] = useState<ActionPlanExecutionBoardItem | null>(null);
  const [editCard, setEditCard] = useState<ActionPlanExecutionBoardItem | null>(null);
  const [logsCard, setLogsCard] = useState<ActionPlanExecutionBoardItem | null>(null);
  const [activeCardId, setActiveCardId] = useState<string | null>(null);
  const [dragOverColumn, setDragOverColumn] = useState<OperationalBoardStatus | null>(null);
  const [editForm, setEditForm] = useState<RiskInventoryOperationalActionPlanItemUpdateIn | null>(null);

  const boardQ = useActionPlanExecutionBoard({
    inventory_id: filters.inventory_id !== "all" ? filters.inventory_id : undefined,
    action_plan_id: filters.action_plan_id !== "all" ? filters.action_plan_id : undefined,
    status: filters.status !== "all" ? filters.status : undefined,
    priority: filters.priority !== "all" ? filters.priority : undefined,
    responsible_user_id:
      filters.responsible_user_id !== "all" ? filters.responsible_user_id : undefined,
    deadline_state: filters.deadline_state !== "all" ? filters.deadline_state : undefined,
    q: filters.q.trim() || undefined,
  });
  const inventoriesQ = useRiskInventories({});
  const responsibleQ = useActionPlanExecutionResponsibleUsers();
  const plansQ = useActionPlans({});
  const setStatusM = useSetInventoryItemActionPlanItemStatus();
  const updateItemM = useUpdateInventoryItemActionPlanItem();
  const createAuditLogM = useCreateActionPlanExecutionItemAuditLog();
  const logsQ = useActionPlanExecutionItemAuditLogs(logsCard?.id);

  const cards = boardQ.data || [];
  const [localCards, setLocalCards] = useState<ActionPlanExecutionBoardItem[]>([]);
  const [recentlyMovedCardId, setRecentlyMovedCardId] = useState<string | null>(null);

  useEffect(() => {
    setLocalCards(cards);
  }, [cards]);

  useEffect(() => {
    if (!recentlyMovedCardId) return;
    const timer = window.setTimeout(() => setRecentlyMovedCardId(null), 1800);
    return () => window.clearTimeout(timer);
  }, [recentlyMovedCardId]);

  const activeCard = useMemo(
    () => localCards.find((item) => item.id === activeCardId) || null,
    [activeCardId, localCards],
  );

  const counts = useMemo(
    () => ({
      total: localCards.length,
      not_started: localCards.filter((item) => item.status === "not_started").length,
      in_progress: localCards.filter((item) => item.status === "in_progress").length,
      completed: localCards.filter((item) => item.status === "completed").length,
      cancelled: localCards.filter((item) => item.status === "cancelled").length,
      overdue: localCards.filter((item) => item.is_overdue).length,
    }),
    [localCards],
  );

  const grouped = useMemo(() => {
    const buckets: Record<OperationalBoardStatus, ActionPlanExecutionBoardItem[]> = {
      not_started: [],
      in_progress: [],
      completed: [],
      cancelled: [],
    };

    localCards.forEach((item) => buckets[item.status].push(item));
    return buckets;
  }, [localCards]);

  function responsibleNameById(id?: string | null) {
    if (!id) return null;
    return (responsibleQ.data || []).find((user) => user.id === id)?.name || null;
  }

  async function createExecutionLog(
    itemId: string,
    action: string,
    eventData: Record<string, unknown>,
  ) {
    const previous = eventData.previous && typeof eventData.previous === "object"
      ? (eventData.previous as Record<string, unknown>)
      : {};
    const current = eventData.current && typeof eventData.current === "object"
      ? (eventData.current as Record<string, unknown>)
      : {};

    const sanitizedPrevious: Record<string, unknown> = {};
    const sanitizedCurrent: Record<string, unknown> = {};
    const keys = new Set([...Object.keys(previous), ...Object.keys(current)]);
    keys.forEach((key) => {
      const prevVal = previous[key] ?? null;
      const currVal = current[key] ?? null;
      if (prevVal !== currVal) {
        sanitizedPrevious[key] = prevVal;
        sanitizedCurrent[key] = currVal;
      }
    });

    if (!Object.keys(sanitizedPrevious).length && !Object.keys(sanitizedCurrent).length) return;

    await createAuditLogM.mutateAsync({
      itemId,
      body: {
        action,
        event_data: { previous: sanitizedPrevious, current: sanitizedCurrent },
      },
    });
  }

  async function refreshBoard() {
    await qc.invalidateQueries({ queryKey: ["action_plan_execution_board"] });
  }

  async function moveCard(card: ActionPlanExecutionBoardItem, nextStatus: OperationalBoardStatus) {
    if (card.status === nextStatus) return;

    await setStatusM.mutateAsync({
      itemId: card.inventory_item_id,
      planId: card.action_plan_id,
      planItemId: card.id,
      status: nextStatus,
    });

    await createExecutionLog(card.id, "update", {
      previous: { status: card.status },
      current: { status: nextStatus },
    });

    await refreshBoard();
    setDetailCard(null);
    setActiveCardId(null);
    setDragOverColumn(null);
    toast.success(`Ação movida para ${statusLabel(nextStatus)}.`);
  }

  function openEdit(card: ActionPlanExecutionBoardItem) {
    setDetailCard(null);
    setEditCard(card);
    setEditForm({
      title: card.title,
      description: card.description || null,
      priority: (card.priority as OperationalPlanPriority) || "medium",
      sort_order: 0,
      expected_result: card.expected_result || null,
      status: card.status,
      responsible_user_id: card.responsible_user_id || null,
      start_date: card.start_date || null,
      due_date: card.due_date || null,
    });
  }

  async function saveEdit() {
    if (!editCard || !editForm) return;

    await updateItemM.mutateAsync({
      itemId: editCard.inventory_item_id,
      planId: editCard.action_plan_id,
      planItemId: editCard.id,
      body: editForm,
    });

    const previous: Record<string, unknown> = {};
    const current: Record<string, unknown> = {};

    if (editCard.title !== editForm.title) {
      previous.title = editCard.title;
      current.title = editForm.title;
    }
    if ((editCard.description || null) !== (editForm.description || null)) {
      previous.description = editCard.description || null;
      current.description = editForm.description || null;
    }
    if (editCard.priority !== editForm.priority) {
      previous.priority = editCard.priority;
      current.priority = editForm.priority;
    }
    if (editCard.status !== editForm.status) {
      previous.status = editCard.status;
      current.status = editForm.status;
    }
    if ((editCard.start_date || null) !== (editForm.start_date || null)) {
      previous.start_date = editCard.start_date || null;
      current.start_date = editForm.start_date || null;
    }
    if ((editCard.due_date || null) !== (editForm.due_date || null)) {
      previous.due_date = editCard.due_date || null;
      current.due_date = editForm.due_date || null;
    }
    if ((editCard.expected_result || null) !== (editForm.expected_result || null)) {
      previous.expected_result = editCard.expected_result || null;
      current.expected_result = editForm.expected_result || null;
    }
    if ((editCard.responsible_user_id || null) !== (editForm.responsible_user_id || null)) {
      previous.responsible_user_name = editCard.responsible_user_name || null;
      current.responsible_user_name = responsibleNameById(editForm.responsible_user_id) || null;
    }

    if (Object.keys(previous).length || Object.keys(current).length) {
      await createExecutionLog(editCard.id, "update", { previous, current });
    }

    await refreshBoard();
    toast.success("Ação atualizada com sucesso.");
    setEditCard(null);
    setEditForm(null);
  }

  function handleDragStart(event: DragStartEvent) {
    const rawId = String(event.active.id);
    if (rawId.startsWith("card:")) {
      setActiveCardId(rawId.replace("card:", ""));
    }
  }

  function handleDragOver(columnKey: OperationalBoardStatus) {
    setDragOverColumn(columnKey);
  }

  async function handleDragEnd(event: DragEndEvent) {
    const rawActive = String(event.active.id);
    const rawOver = event.over ? String(event.over.id) : null;

    setDragOverColumn(null);

    if (!rawActive.startsWith("card:")) {
      setActiveCardId(null);
      return;
    }

    const cardId = rawActive.replace("card:", "");
    const card = localCards.find((item) => item.id === cardId);

    if (!card || !rawOver || !rawOver.startsWith("column:")) {
      setActiveCardId(null);
      return;
    }

    const nextStatus = rawOver.replace("column:", "") as OperationalBoardStatus;

    if (card.status === nextStatus) {
      setActiveCardId(null);
      setRecentlyMovedCardId(card.id);
      return;
    }

    setLocalCards((prev) =>
      prev.map((item) => (item.id === card.id ? { ...item, status: nextStatus } : item)),
    );
    setRecentlyMovedCardId(card.id);
    await moveCard(card, nextStatus);
  }

  return (
    <div className="space-y-5">
      <PageHeader
        title="Execução de Planos de Ação"
        description="Board operacional das ações derivadas dos planos vinculados aos itens do inventário."
      />

      <div className="grid gap-3 md:grid-cols-6">
        <SmallStat label="Total" value={counts.total} />
        <SmallStat label="Não iniciadas" value={counts.not_started} />
        <SmallStat label="Em andamento" value={counts.in_progress} />
        <SmallStat label="Concluídas" value={counts.completed} />
        <SmallStat label="Canceladas" value={counts.cancelled} />
        <SmallStat label="Vencidas" value={counts.overdue} />
      </div>

      <Card className="shadow-sm">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <Filter className="h-4 w-4" /> Filtros do board
          </CardTitle>
        </CardHeader>
        <CardContent className="overflow-x-auto pb-4">
          <div className="flex min-w-[1320px] items-end gap-3 whitespace-nowrap">
            <div className="min-w-[320px] flex-[1.7]">
              <Label>Buscar</Label>
              <div className="relative mt-1">
                <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  className="pl-9"
                  value={filters.q}
                  onChange={(e) => setFilters((p) => ({ ...p, q: e.target.value }))}
                  placeholder="Ação, plano, item ou risco"
                />
              </div>
            </div>
            <div className="min-w-[180px] flex-1">
              <Label>Inventário</Label>
              <Select
                value={filters.inventory_id}
                onValueChange={(value) => setFilters((p) => ({ ...p, inventory_id: value }))}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  {(inventoriesQ.data || []).map((inventory) => (
                    <SelectItem key={inventory.id} value={inventory.id}>
                      {inventory.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="min-w-[180px] flex-1">
              <Label>Plano</Label>
              <Select
                value={filters.action_plan_id}
                onValueChange={(value) => setFilters((p) => ({ ...p, action_plan_id: value }))}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  {(plansQ.data || []).map((plan) => (
                    <SelectItem key={plan.id} value={plan.id}>
                      {plan.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="min-w-[160px] flex-1">
              <Label>Status</Label>
              <Select
                value={filters.status}
                onValueChange={(value) => setFilters((p) => ({ ...p, status: value }))}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  {COLUMNS.map((column) => (
                    <SelectItem key={column.key} value={column.key}>
                      {column.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="min-w-[160px] flex-1">
              <Label>Prioridade</Label>
              <Select
                value={filters.priority}
                onValueChange={(value) => setFilters((p) => ({ ...p, priority: value }))}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas</SelectItem>
                  <SelectItem value="low">Baixa</SelectItem>
                  <SelectItem value="medium">Média</SelectItem>
                  <SelectItem value="high">Alta</SelectItem>
                  <SelectItem value="critical">Crítica</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="min-w-[180px] flex-1">
              <Label>Responsável</Label>
              <Select
                value={filters.responsible_user_id}
                onValueChange={(value) => setFilters((p) => ({ ...p, responsible_user_id: value }))}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  {(responsibleQ.data || []).map((user) => (
                    <SelectItem key={user.id} value={user.id}>
                      {user.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="min-w-[160px] flex-1">
              <Label>Prazo</Label>
              <Select
                value={filters.deadline_state}
                onValueChange={(value) => setFilters((p) => ({ ...p, deadline_state: value }))}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="overdue">Vencidas</SelectItem>
                  <SelectItem value="on_time">No prazo</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCorners}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDragCancel={() => {
          setActiveCardId(null);
          setDragOverColumn(null);
        }}
      >
        <div className="grid items-start gap-4 xl:grid-cols-4">
          {COLUMNS.map((column) => (
            <Card
              key={column.key}
              className={`flex h-[calc(100vh-20rem)] min-h-[560px] flex-col border-muted/70 shadow-sm transition ${
                dragOverColumn === column.key ? "ring-2 ring-primary/20" : ""
              }`}
            >
              <CardHeader className="border-b border-border/60 pb-3">
                <CardTitle className="flex items-center justify-between text-sm font-semibold">
                  <span>{column.title}</span>
                  <Badge variant="secondary" className="rounded-full px-2.5 py-0.5">
                    {grouped[column.key].length}
                  </Badge>
                </CardTitle>
              </CardHeader>
              <ColumnDropZone
                columnKey={column.key}
                active={dragOverColumn === column.key}
                onDragOver={() => handleDragOver(column.key)}
              >
                {grouped[column.key]
                  .filter((card) => card.id !== activeCardId)
                  .map((card) => (
                    <ActionBoardCard
                      key={card.id}
                      card={card}
                      onDetail={setDetailCard}
                      onEdit={openEdit}
                      onLogs={setLogsCard}
                      isHighlighted={recentlyMovedCardId === card.id}
                    />
                  ))}
              </ColumnDropZone>
            </Card>
          ))}
        </div>

        <DragOverlay dropAnimation={{ duration: 180, easing: "ease" }}>
          {activeCard ? (
            <div className="w-[320px] max-w-[90vw]">
              <ActionBoardCard card={activeCard} onDetail={setDetailCard} onEdit={openEdit} onLogs={setLogsCard} isOverlay isHighlighted />
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>

      <Dialog open={!!detailCard} onOpenChange={(open) => !open && setDetailCard(null)}>
        <DialogContent className="sm:max-w-2xl">
          {detailCard ? (
            <>
              <DialogHeader>
                <DialogTitle className="text-lg">Detalhes da ação</DialogTitle>
                <DialogDescription>
                  {detailCard.plan_title} • {detailCard.inventory_item_title}
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-4 py-1">
                <div className="rounded-xl border bg-muted/20 p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <h3 className="text-sm font-semibold leading-5">{detailCard.title}</h3>
                      <p className="mt-1 text-xs text-muted-foreground">
                        {detailCard.risk_name || "Sem risco informado"}
                      </p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <Badge variant="outline">{priorityLabel(detailCard.priority)}</Badge>
                      <Badge variant="secondary">
                        {detailCard.item_origin === "template" ? "Template" : "Manual"}
                      </Badge>
                    </div>
                  </div>
                </div>

                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-3 rounded-xl border p-4">
                    <div>
                      <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Descrição</p>
                      <p className="mt-1 text-sm">{detailCard.description || "-"}</p>
                    </div>
                    <Separator />
                    <div>
                      <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Resultado esperado</p>
                      <p className="mt-1 text-sm">{detailCard.expected_result || "-"}</p>
                    </div>
                  </div>

                  <div className="space-y-3 rounded-xl border p-4">
                    <div>
                      <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Status</p>
                      <p className="mt-1 text-sm">{statusLabel(detailCard.status)}</p>
                    </div>
                    <Separator />
                    <div>
                      <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Responsável</p>
                      <p className="mt-1 text-sm">{detailCard.responsible_user_name || "-"}</p>
                    </div>
                    <Separator />
                    <div>
                      <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Início / Prazo</p>
                      <p className="mt-1 text-sm">
                        {formatDateBR(detailCard.start_date)} • {formatDateBR(detailCard.due_date)}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setDetailCard(null)}>
                  Fechar
                </Button>
              </DialogFooter>
            </>
          ) : null}
        </DialogContent>
      </Dialog>

      <Dialog open={!!logsCard} onOpenChange={(open) => !open && setLogsCard(null)}>
        <DialogContent className="sm:max-w-4xl">
          <DialogHeader>
            <DialogTitle>Logs da ação</DialogTitle>
            <DialogDescription>
              {logsCard ? `${logsCard.title} • ${logsCard.plan_title}` : ""}
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[60vh] overflow-auto rounded-xl border">
            <div className="grid min-w-[880px] grid-cols-[170px_180px_110px_1fr_1fr] border-b bg-muted/40 px-4 py-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              <div>Data/Hora</div>
              <div>Usuário</div>
              <div>Tipo</div>
              <div>De</div>
              <div>Para</div>
            </div>
            {logsQ.isLoading ? (
              <div className="p-4 text-sm text-muted-foreground">Carregando logs...</div>
            ) : (logsQ.data || []).filter(hasMeaningfulAuditData).length ? (
              (logsQ.data || []).filter(hasMeaningfulAuditData).map((log) => (
                <div
                  key={log.id}
                  className="grid min-w-[880px] grid-cols-[170px_180px_110px_1fr_1fr] items-start gap-3 border-b px-4 py-3 text-sm last:border-b-0"
                >
                  <div className="text-muted-foreground">{formatDateTimeBR(log.created_at)}</div>
                  <div>{log.created_by_name || "-"}</div>
                  <div>
                    <Badge variant="outline">{typeLabel(log.action)}</Badge>
                  </div>
                  <pre className="whitespace-pre-wrap break-words font-sans text-sm">{parseAuditBlock(log, "previous")}</pre>
                  <pre className="whitespace-pre-wrap break-words font-sans text-sm">{parseAuditBlock(log, "current")}</pre>
                </div>
              ))
            ) : (
              <div className="p-4 text-sm text-muted-foreground">Nenhum log encontrado.</div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLogsCard(null)}>Fechar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={!!editCard}
        onOpenChange={(open) => {
          if (!open) {
            setEditCard(null);
            setEditForm(null);
          }
        }}
      >
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>Editar ação operacional</DialogTitle>
            <DialogDescription>
              Atualize os dados operacionais da ação sem alterar o plano-base.
            </DialogDescription>
          </DialogHeader>
          {editForm ? (
            <div className="grid gap-4 py-2">
              <div className="grid gap-2">
                <Label>Título</Label>
                <Input
                  value={editForm.title}
                  onChange={(e) =>
                    setEditForm((prev) => (prev ? { ...prev, title: e.target.value } : prev))
                  }
                />
              </div>
              <div className="grid gap-2">
                <Label>Descrição</Label>
                <Textarea
                  value={editForm.description || ""}
                  onChange={(e) =>
                    setEditForm((prev) => (prev ? { ...prev, description: e.target.value } : prev))
                  }
                  rows={4}
                />
              </div>
              <div className="grid gap-4 md:grid-cols-3">
                <div className="grid gap-2">
                  <Label>Prioridade</Label>
                  <Select
                    value={editForm.priority}
                    onValueChange={(value) =>
                      setEditForm((prev) =>
                        prev ? { ...prev, priority: value as OperationalPlanPriority } : prev,
                      )
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Baixa</SelectItem>
                      <SelectItem value="medium">Média</SelectItem>
                      <SelectItem value="high">Alta</SelectItem>
                      <SelectItem value="critical">Crítica</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid gap-2">
                  <Label>Status</Label>
                  <Select
                    value={editForm.status}
                    onValueChange={(value) =>
                      setEditForm((prev) =>
                        prev ? { ...prev, status: value as OperationalBoardStatus } : prev,
                      )
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {COLUMNS.map((column) => (
                        <SelectItem key={column.key} value={column.key}>
                          {column.title}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid gap-2">
                  <Label>Responsável</Label>
                  <Select
                    value={editForm.responsible_user_id || "none"}
                    onValueChange={(value) =>
                      setEditForm((prev) =>
                        prev
                          ? {
                              ...prev,
                              responsible_user_id: value === "none" ? null : value,
                            }
                          : prev,
                      )
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">-</SelectItem>
                      {(responsibleQ.data || []).map((user) => (
                        <SelectItem key={user.id} value={user.id}>
                          {user.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid gap-4 md:grid-cols-2">
                <div className="grid gap-2">
                  <Label>Início</Label>
                  <Input
                    type="date"
                    value={editForm.start_date || ""}
                    onChange={(e) =>
                      setEditForm((prev) =>
                        prev ? { ...prev, start_date: e.target.value || null } : prev,
                      )
                    }
                  />
                </div>
                <div className="grid gap-2">
                  <Label>Prazo</Label>
                  <Input
                    type="date"
                    value={editForm.due_date || ""}
                    onChange={(e) =>
                      setEditForm((prev) =>
                        prev ? { ...prev, due_date: e.target.value || null } : prev,
                      )
                    }
                  />
                </div>
              </div>
              <div className="grid gap-2">
                <Label>Resultado esperado</Label>
                <Textarea
                  value={editForm.expected_result || ""}
                  onChange={(e) =>
                    setEditForm((prev) =>
                      prev ? { ...prev, expected_result: e.target.value } : prev,
                    )
                  }
                  rows={3}
                />
              </div>
            </div>
          ) : null}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setEditCard(null);
                setEditForm(null);
              }}
            >
              Cancelar
            </Button>
            <Button onClick={saveEdit} disabled={updateItemM.isPending}>
              Salvar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ColumnDropZone({
  columnKey,
  children,
  active,
  onDragOver,
}: {
  columnKey: OperationalBoardStatus;
  children: ReactNode;
  active: boolean;
  onDragOver: () => void;
}) {
  const { setNodeRef, isOver } = useDroppable({ id: `column:${columnKey}` });

  return (
    <CardContent
      ref={setNodeRef}
      className={`flex-1 space-y-3 overflow-x-hidden overflow-y-auto p-3 transition-colors ${
        active || isOver ? "bg-primary/5" : ""
      }`}
      onMouseEnter={onDragOver}
    >
      {children}
    </CardContent>
  );
}
